import { Injectable } from '@angular/core';
import { ReplaySubject, of, timer } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

export interface NotificationAction {
  label: string;
  run: () => void;
}

export interface Notification {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  action?: NotificationAction;
}

export interface ShowOptions {
  action?: NotificationAction;
  /** Overrides the type's default auto-dismiss duration. `null` disables auto-dismiss. */
  durationMs?: number | null;
}

const DISMISS_DURATIONS: Record<Notification['type'], number | null> = {
  success: 4000,
  info: 5000,
  warning: 8000,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  // Replay so a toast raised before the notification component subscribes (e.g. during bootstrap) is not lost.
  private readonly _show$ = new ReplaySubject<{ notification: Notification | null; duration: number | null }>(1);

  public notification$ = this._show$.pipe(
    switchMap(({ notification, duration }) =>
      notification && duration !== null
        ? timer(duration).pipe(
            map(() => null),
            startWith(notification),
          )
        : of(notification),
    ),
    startWith(null),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  show(message: string, type: Notification['type'] = 'error', options?: ShowOptions) {
    const duration = options?.durationMs !== undefined ? options.durationMs : DISMISS_DURATIONS[type];
    this._show$.next({ notification: { message, type, action: options?.action }, duration });
  }

  hide() {
    this._show$.next({ notification: null, duration: null });
  }
}
